/**
 * Recipe-based automation rules (#1100 / epic #1086).
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/hooks/useNotify';
import {
  useAutomationRecipes,
  useAutomationRules,
  useAutomationSettings,
  useUpdateAutomationSettings,
  type AutomationRule,
} from '@/hooks/useAutomation';
import { CreateRuleDialog } from './CreateRuleDialog';
import { RuleDetailDialog } from './RuleDetailDialog';

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  dry_run: 'info',
  active: 'success',
  paused: 'warning',
  disabled: 'default',
};

export default function AutomationsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation('automation');
  const notify = useNotify();
  const canManage = useAuthStore((s) => s.hasPermission('automation.rules.manage'));
  const [searchParams, setSearchParams] = useSearchParams();
  const createRequested = searchParams.get('create');
  const insightId = searchParams.get('insightId');
  const insightType = searchParams.get('insightType');

  const rulesQuery = useAutomationRules();
  const recipesQuery = useAutomationRecipes();
  const settingsQuery = useAutomationSettings();
  const updateSettings = useUpdateAutomationSettings();

  const [createOpen, setCreateOpen] = useState(Boolean(createRequested));
  const [selected, setSelected] = useState<AutomationRule | null>(null);

  const recipesById = useMemo(
    () => new Map((recipesQuery.data ?? []).map((recipe) => [recipe.id, recipe])),
    [recipesQuery.data],
  );

  const settings = settingsQuery.data;
  const rules = rulesQuery.data ?? [];

  const closeCreate = () => {
    setCreateOpen(false);
    if (createRequested || insightId) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      next.delete('insightId');
      next.delete('insightType');
      setSearchParams(next, { replace: true });
    }
  };

  const toggleSetting = async (patch: { killSwitch?: boolean; paused?: boolean }) => {
    try {
      await updateSettings.mutateAsync(patch);
    } catch {
      notify.error(t('transitionFailed'));
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          {!embedded && <Typography variant="h4" fontWeight={600}>{t('title')}</Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('subtitle')}
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            {t('create')}
          </Button>
        )}
      </Box>

      {settings?.killSwitch && <Alert severity="error" sx={{ mb: 2 }}>{t('killSwitchOn')}</Alert>}
      {settings?.paused && !settings.killSwitch && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('tenantPaused')}</Alert>
      )}

      {canManage && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(settings?.killSwitch)}
                onChange={(e) => void toggleSetting({ killSwitch: e.target.checked })}
                disabled={updateSettings.isPending}
              />
            }
            label={t('toggleKillSwitch')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(settings?.paused)}
                onChange={(e) => void toggleSetting({ paused: e.target.checked })}
                disabled={updateSettings.isPending}
              />
            }
            label={t('togglePaused')}
          />
        </Stack>
      )}

      {rulesQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6">{t('noRules')}</Typography>
          <Typography color="text.secondary">{t('noRulesDesc')}</Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {rules.map((rule) => {
            const recipe = recipesById.get(rule.recipeId);
            return (
              <Card key={rule.id} variant="outlined">
                <CardActionArea onClick={() => setSelected(rule)}>
                  <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" noWrap>{rule.name}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {recipe ? t(recipe.titleKey.replace(/^automation\./, '')) : rule.recipeId}
                      </Typography>
                    </Box>
                    <Chip size="small" color={STATUS_COLOR[rule.status]} label={t(`status.${rule.status}`)} />
                    {rule.needsReapproval && <Chip size="small" color="warning" label={t('reapprove')} />}
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}

      <CreateRuleDialog
        open={createOpen}
        onClose={closeCreate}
        preselectedRecipeId={createRequested}
        insightPrefill={
          insightId && insightType ? { insightId, insightType } : null
        }
      />
      <RuleDetailDialog
        rule={selected}
        onClose={() => setSelected(null)}
        killSwitch={Boolean(settings?.killSwitch)}
      />
    </Box>
  );
}
